/* eslint-disable import/prefer-default-export */
import axios from '../axios';

// ---------------------------------------------------------------------------
// Comercial / Clientes — Service Layer
//
// Camada fina sobre o axios singleton. Faz parsing do envelope JSend e renomeia
// campos snake_case → camelCase apenas nas FRONTEIRAS (paginação, raiz do
// envelope). Campos das linhas (name, doc, person_type, etc.) ficam em
// snake_case para preservar simetria com o backend.
//
// Tratamento de erro NÃO acontece aqui — é responsabilidade das páginas
// (toast, fieldErrors). O recurso de backend é `customers` (permissões
// customers:customer:*), embora o módulo de menu se chame "Comercial".
// ---------------------------------------------------------------------------

// ===========================================================================
// CLIENTES
// ===========================================================================

export async function listCustomers(filters = {}) {
   // Filtros vão como query params (GET), todos opcionais e combinados com AND
   // no backend (handoff 0029 §2.1). Só envia os não-vazios — string vazia o
   // backend ignora, mas mantemos a URL/o request limpos. A tradução
   // camelCase → snake_case acontece SÓ aqui (boundary do service).
   const {
      page,
      perPage,
      search,
      name,
      doc,
      refiltekId,
      email,
      phone,
      personType,
      status,
      createdFrom,
      createdTo,
      orderBy,
      orderDir,
   } = filters;

   const params = {};
   if (page) params.page = page;
   if (perPage) params.per_page = perPage;
   if (search) params.search = search;
   if (name) params.name = name;
   if (doc) params.doc = doc;
   if (refiltekId) params.refiltek_id = refiltekId;
   if (email) params.email = email;
   if (phone) params.phone = phone;
   if (personType) params.person_type = personType;
   if (status) params.status = status;
   if (createdFrom) params.created_from = createdFrom;
   if (createdTo) params.created_to = createdTo;
   if (orderBy) params.order_by = orderBy;
   if (orderDir) params.order_dir = orderDir;

   const response = await axios.get('/customers', { params });
   const envelope = response?.data ?? {};
   const data = envelope.data ?? {};
   const meta = envelope.meta ?? {};

   return {
      customers: data.customers ?? [],
      total: meta.total ?? 0,
      page: meta.page ?? 1,
      perPage: meta.per_page ?? 20,
      totalPages: meta.total_pages ?? 1,
   };
}

// Autocomplete leve — retorna [{ id, name, doc }]. Base do CustomerNameSearch
// (Routing e billing reusam no futuro).
export async function searchCustomersByName(query, limit = 15) {
   const response = await axios.get('/customers/search-name', {
      params: { q: query, limit },
   });
   return response?.data?.data?.customers ?? [];
}

// Detalhe — único endpoint que devolve `addresses` aninhado.
export async function getCustomer(customerId) {
   const response = await axios.get(`/customers/${customerId}`);
   return response?.data?.data?.customer ?? null;
}

export async function createCustomer(payload) {
   const response = await axios.post('/customers', payload);
   return {
      customer: response?.data?.data?.customer ?? null,
      message: response?.data?.message ?? 'Cliente criado com sucesso.',
   };
}

export async function updateCustomer(customerId, payload) {
   const response = await axios.put(`/customers/${customerId}`, payload);
   return {
      customer: response?.data?.data?.customer ?? null,
      message: response?.data?.message ?? 'Cliente atualizado com sucesso.',
   };
}

// Soft-delete — backend responde 204 sem body, então não há nada para ler.
export async function deleteCustomer(customerId) {
   await axios.delete(`/customers/${customerId}`);
}

// ===========================================================================
// ENDEREÇOS (aninhados em /customers/:customerId/addresses)
// ===========================================================================

export async function listAddresses(customerId) {
   const response = await axios.get(`/customers/${customerId}/addresses`);
   // Backend devolve o principal primeiro.
   return response?.data?.data?.addresses ?? [];
}

export async function createAddress(customerId, payload) {
   const response = await axios.post(
      `/customers/${customerId}/addresses`,
      payload,
   );
   return {
      address: response?.data?.data?.address ?? null,
      message: response?.data?.message ?? 'Endereço criado com sucesso.',
   };
}

export async function updateAddress(customerId, addressId, payload) {
   const response = await axios.put(
      `/customers/${customerId}/addresses/${addressId}`,
      payload,
   );
   return {
      address: response?.data?.data?.address ?? null,
      message: response?.data?.message ?? 'Endereço atualizado com sucesso.',
   };
}

// Soft-delete — 204 sem body.
export async function deleteAddress(customerId, addressId) {
   await axios.delete(`/customers/${customerId}/addresses/${addressId}`);
}
